import React, { useEffect, useRef, useState } from 'react';
import brandConfig from '../config/brand';
import { CHEM_BRUSH } from '../utils/serviceOptions';
import '../styles/tier-picker.css';

const serviceTiers = {
  'monthly-pool-chemicals-only': 'chem_only',
  'monthly-pool-chem-brush': 'chem_brush',
  'monthly-pool-service': 'full',
};
// Rows a tier lacks are shown muted in the comparison, aligned with the Full
// Service row they correspond to. Every tier list is a row-for-row prefix of
// the Full Service list (see tierPickerIncludes).
const comparisonRows = tier => {
  const included = brandConfig.tierPickerIncludes[tier];
  return brandConfig.tierPickerIncludes.full.map((item, i) => (i < included.length
    ? { label: included[i], included: true } : { label: item, included: false }));
};
const money = amount => new Intl.NumberFormat('en-US', {
  style: 'currency', currency: 'USD', maximumFractionDigits: 2,
}).format(amount);

export default function TierPicker({ offer, currentData, onVisible, onAccept, onSelection }) {
  const root = useRef(null);
  const visibilityStarted = useRef(false);
  const submitting = useRef(false);
  const [selected, setSelected] = useState(currentData.serviceOption?.serviceId || CHEM_BRUSH);
  const [selectedExtras, setSelectedExtras] = useState({});
  const [email, setEmail] = useState(currentData.email || '');
  const [visibleSaved, setVisibleSaved] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const options = offer.arm === 'A' ? offer.options.filter(o => o.serviceId === CHEM_BRUSH) : offer.options;
  const selectedOption = options.find(o => o.serviceId === selected);

  const saveVisibility = async () => {
    setError('');
    try {
      await onVisible(offer);
      setVisibleSaved(true);
    } catch (_) {
      setError('We could not save your quote yet. Please retry to continue.');
    }
  };

  useEffect(() => {
    let intersects = false;
    const checkVisibility = () => {
      if (intersects && document.visibilityState === 'visible'
          && !document.querySelector('.cabana-popup.show') && !visibilityStarted.current) {
        visibilityStarted.current = true;
        saveVisibility();
      }
    };
    const observer = new IntersectionObserver(entries => {
      intersects = entries.some(entry => entry.isIntersecting);
      checkVisibility();
    }, { threshold: 0.1 });
    const popupObserver = new MutationObserver(checkVisibility);
    popupObserver.observe(document.body, { childList: true, subtree: true, attributes: true, attributeFilter: ['class'] });
    document.addEventListener('visibilitychange', checkVisibility);
    observer.observe(root.current);
    return () => {
      observer.disconnect();
      popupObserver.disconnect();
      document.removeEventListener('visibilitychange', checkVisibility);
    };
    // Each immutable offer has one visibility boundary, independent of selection.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [offer.id]);

  const choose = serviceId => {
    setSelected(serviceId);
    onSelection({ serviceId, selectedExtras: selectedExtras[serviceId] || {} });
  };

  const accept = async event => {
    event.preventDefault();
    if (submitting.current || !visibleSaved || !selectedOption) return;
    submitting.current = true;
    setBusy(true);
    setError('');
    try {
      await onAccept({
        email, amount: selectedOption.monthlyAmount,
        serviceOption: { offerId: offer.id, serviceId: selected, selectedExtras: selectedExtras[selected] || {} },
      });
    } catch (err) {
      setError(err.message || 'We could not confirm your request. Please try again.');
    } finally {
      submitting.current = false;
      setBusy(false);
    }
  };

  return <section className="tp-root" ref={root} aria-labelledby="service-option-title">
    <header className="tp-header">
      <h1 className="tp-title" id="service-option-title">{offer.arm === 'A' ? 'Your pool service quote' : 'Choose your pool service'}</h1>
      <p className="tp-subtitle">A clean, safe pool every week, with photo proof after every visit.</p>
      <ul className="tp-trust" aria-label={`Why homeowners choose ${brandConfig.name}`}>
        <li><strong>Since 1995</strong> serving Sacramento</li>
        <li><strong>{brandConfig.socialProofTotal}</strong> pools served</li>
        <li><strong>4.9 &#9733;</strong> Google rating</li>
        <li><strong>CPO</strong> certified technicians</li>
        <li><strong>Licensed</strong> CSLB #1133031, <strong>$2M</strong> insured</li>
      </ul>
    </header>
    <form onSubmit={accept}>
      <fieldset className={`tp-cards ${offer.arm === 'A' ? 'tp-single' : ''}`} disabled={busy}>
        <legend className="tp-sr-only">Service options</legend>
        {options.map(option => <div className={`tp-card ${selected === option.serviceId ? 'tp-card-highlighted' : ''} ${offer.arm === 'B' ? 'tp-card-selectable' : ''}`}
          key={option.serviceId} style={{ borderColor: selected === option.serviceId ? brandConfig.colors.primary : undefined }}
          onClick={event => {
            // The radio and extras handle their own clicks; anywhere else on the card selects it.
            if (offer.arm !== 'B' || busy || selected === option.serviceId || event.target.closest('label, input')) return;
            choose(option.serviceId);
          }}>
          <label className="tp-card-choice">
            {offer.arm === 'B' && <input type="radio" name="service_option" value={option.serviceId}
              checked={selected === option.serviceId} onChange={() => choose(option.serviceId)} />}
            <span className="tp-card-headline">{option.name}</span>
          </label>
          {offer.arm === 'B' && option.serviceId === CHEM_BRUSH && <span className="tp-badge"
            style={{ backgroundColor: brandConfig.colors.primary }}>Recommended</span>}
          <p className="tp-best-for">{brandConfig.tierPickerBestFor[serviceTiers[option.serviceId]]}</p>
          <p className="tp-price-block"><span className="tp-price-amount">{money(option.monthlyAmount)}</span>
            <span className="tp-price-cadence"> / month</span></p>
          <ul className="tp-bullets">{comparisonRows(serviceTiers[option.serviceId])
            .filter(row => row.included || offer.arm === 'B')
            .map(row => (row.included ? <li key={row.label}>{row.label}</li>
              : <li key={row.label} className="tp-excluded"><span className="tp-sr-only">Not included: </span>{row.label}</li>))}</ul>
          {option.disclosure && <p className="tp-fineprint">{option.disclosure}</p>}
          {(option.extras || []).map(extra => <label className="tp-extra" key={extra.id}>
            <input type="checkbox" checked={!!selectedExtras[option.serviceId]?.[extra.id]} onChange={event => {
              const next = { ...selectedExtras, [option.serviceId]: {
                ...selectedExtras[option.serviceId], [extra.id]: event.target.checked,
              } };
              // Ticking an extra also selects the card it belongs to.
              setSelectedExtras(next);
              setSelected(option.serviceId);
              onSelection({ serviceId: option.serviceId, selectedExtras: next[option.serviceId] || {} });
            }} /> {extra.name}: {money(extra.amount)} {extra.frequency}
          </label>)}
        </div>)}
      </fieldset>
      <section className="tp-reviews" aria-labelledby="tp-reviews-title">
        <h2 className="tp-reviews-title" id="tp-reviews-title">What Sacramento-area customers say</h2>
        <div className="tp-review-list">{brandConfig.sacReviews.map(review => <figure className="tp-review" key={review.name}>
          <div className="tp-review-stars" aria-label="5 out of 5 stars">&#9733;&#9733;&#9733;&#9733;&#9733;</div>
          <blockquote>{review.text}</blockquote>
          <figcaption>{review.name}, {review.source} review</figcaption>
        </figure>)}</div>
      </section>
      <div className="tp-accept">
        <label htmlFor="service-option-email">Email for your quote</label>
        <input id="service-option-email" type="email" autoComplete="email" required value={email}
          onChange={event => setEmail(event.target.value)} disabled={busy} />
        {error && <p role="alert">{error}</p>}
        {!visibleSaved && error && <button type="button" onClick={saveVisibility}>Retry</button>}
        <button className="tp-select-btn" style={{ backgroundColor: brandConfig.colors.primary }}
          disabled={busy || !visibleSaved} type="submit">{busy ? 'Saving your request...' : 'Request this service'}</button>
        <p className="tp-assurance"><strong>Satisfaction guarantee:</strong> if a visit falls short, we come back and make it right at no charge.
          Month-to-month, cancel anytime with 30 days notice.</p>
        <p className="tp-footnote">We will confirm your service details and start date.</p>
      </div>
    </form>
  </section>;
}
